import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useParams } from 'react-router-dom';

import TableComponent from 'components/partials/table-component/table-component';
import type { TableColumnDef } from 'components/partials/table-component/table-component';
import toast from 'components/partials/toast/toast';

import CollapsibleSection from './components/collapsible-section';
import { getTuitionTableSettings } from './tuition-table-settings';
import { DICTIONARY_TYPE, COA_TOTAL_CATEGORY } from './tuition-step.constants';

import {
  useModelCoa,
  useUpdateModelCoa,
  useInvalidateModelCoa,
} from '../../hooks/use-aid-coa';
import { useInvalidateAidResults } from '../../hooks/use-aid-results';
import { useInvalidateRegimeComparison } from '../../hooks/use-aid-regimes';
import { useCategoryDictionary } from '../../hooks/use-category-dictionary';
import { useTuitionDraft } from './hooks/use-tuition-draft';

import { useCurrentUser } from 'hooks/api/use-current-user';

import type { CoaGroup } from './tuition-step.types';

const buildTableData = (group: CoaGroup): Record<string, unknown>[] => {
  const expenses = (group.expenses || []).map((exp) => ({
    ...exp,
    includeRequiredFees: group.includeRequiredFees,
  }));

  const totalPreviousYear = expenses.reduce(
    (sum, exp) => sum + (Number(exp.previousYearAmount) || 0),
    0
  );
  const totalCurrentYear = expenses.reduce(
    (sum, exp) => sum + (Number(exp.currentYearAmount) || 0),
    0
  );

  return [
    ...expenses,
    {
      category: COA_TOTAL_CATEGORY,
      previousYearAmount: totalPreviousYear,
      currentYearAmount: totalCurrentYear,
      isTotalRow: true,
    },
  ];
};

interface TuitionStepProps {
  readOnly?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

const TuitionStep = forwardRef<unknown, TuitionStepProps>(
  ({ readOnly, onDirtyChange }, ref) => {
    const { modelId } = useParams();
    const currentUser = useCurrentUser() as unknown as {
      data?: { oktaUserId?: string };
    };
    const userId = currentUser?.data?.oktaUserId;

    const [inputsRemountKey, setInputsRemountKey] = useState(0);

    const coaQuery = useModelCoa({
      userId: userId ?? '',
      modelId: modelId ?? '',
      enabled: !!modelId,
    });
    const serverCoa = coaQuery.data as CoaGroup[] | null | undefined;

    const categoryDictionaryQuery = useCategoryDictionary({
      userId: userId ?? '',
      type: DICTIONARY_TYPE,
      enabled: !!userId,
    });
    const categoryLabels = (categoryDictionaryQuery.data ?? {}) as Record<
      string,
      string | undefined
    >;

    const categoryOrder = useMemo(
      () => Object.keys(categoryLabels),
      [categoryLabels]
    );

    const updateCoa = useUpdateModelCoa();
    const invalidateCoa = useInvalidateModelCoa();
    const invalidateAidResults = useInvalidateAidResults();
    const invalidateRegimeComparison = useInvalidateRegimeComparison();

    const {
      coaGroups,
      hasChanges,
      updateAmount,
      updateByPercent,
      updateIncludeFees,
      reset,
      markForReinit,
      getChangedGroups,
    } = useTuitionDraft(serverCoa, categoryOrder);

    const isDraftSyncing =
      (serverCoa?.length ?? 0) > 0 && coaGroups.length === 0;
    const isPageLoading =
      coaQuery.isFetching ||
      categoryDictionaryQuery.isFetching ||
      isDraftSyncing;

    const handleSave = useCallback(async (): Promise<void> => {
      if (!hasChanges) return;

      try {
        const changedGroups = getChangedGroups() as CoaGroup[];

        await Promise.all(
          changedGroups
            .filter((group) => group.id !== undefined)
            .map((group) =>
              updateCoa.mutateAsync({
                userId: userId ?? '',
                modelId: modelId ?? '',
                coaId: group.id as string | number,
                data: {
                  expenses: group.expenses,
                  includeRequiredFees: group.includeRequiredFees,
                },
              })
            )
        );

        await invalidateCoa(modelId ?? '');
        await invalidateAidResults(userId ?? '', modelId ?? '');
        await invalidateRegimeComparison(userId ?? '', modelId ?? '');
        markForReinit();

        toast.success({
          title: 'Tuition updated',
          message: 'Cost of attendance has been saved successfully',
        });
      } catch (error) {
        const err = error as { response?: { data?: { message?: string } } };

        toast.error({
          title: 'Error saving tuition',
          message: err?.response?.data?.message || 'An error occurred',
        });
      }
    }, [
      hasChanges,
      getChangedGroups,
      markForReinit,
      invalidateCoa,
      invalidateAidResults,
      invalidateRegimeComparison,
      userId,
      modelId,
      updateCoa,
    ]);

    const handleDiscard = useCallback(() => {
      setInputsRemountKey((k) => k + 1);
      reset();
    }, [reset]);

    useImperativeHandle(
      ref,
      () => ({ save: handleSave, discard: handleDiscard, hasChanges }),
      [handleSave, handleDiscard, hasChanges]
    );

    useEffect(() => {
      onDirtyChange?.(hasChanges);
    }, [hasChanges, onDirtyChange]);

    const groupIdsKey = (coaGroups as CoaGroup[]).map((g) => g.id).join('|');

    const columnsByGroup = useMemo<
      Record<number, TableColumnDef<Record<string, unknown>>[]>
    >(() => {
      if (!groupIdsKey) return {};

      return groupIdsKey
        .split('|')
        .reduce<Record<number, TableColumnDef<Record<string, unknown>>[]>>(
          (acc, rawId) => {
            const id = Number(rawId);

            acc[id] = getTuitionTableSettings({
              onAmountChange: updateAmount,
              onPercentChange: updateByPercent,
              onIncludeFeesChange: updateIncludeFees,
              coaId: id,
              categoryLabels,
              inputsRemountKey,
            }) as TableColumnDef<Record<string, unknown>>[];

            return acc;
          },
          {}
        );
    }, [
      groupIdsKey,
      updateAmount,
      updateByPercent,
      updateIncludeFees,
      categoryLabels,
      inputsRemountKey,
    ]);

    if (!modelId) {
      return (
        <div className="py-8 text-center text-gray-600">
          Save the model first to configure tuition and cost of attendance.
        </div>
      );
    }

    return (
      <div data-testid="tuition-step">
        {!isPageLoading && (serverCoa?.length ?? 0) === 0 && (
          <div className="py-8 text-center text-gray-600">
            No tuition data available for this model.
          </div>
        )}

        <div className="flex flex-col gap-4">
          {(coaGroups as CoaGroup[]).map((group, index) => (
            <CollapsibleSection
              key={group.id}
              title={group.name || 'Tuition rate'}
              defaultOpen={index === 0}
              data-testid={`coa-section-${group.id}`}
              readOnly={readOnly}
            >
              <TableComponent
                columns={columnsByGroup[group.id as number] || []}
                data={buildTableData(group)}
                isLoading={isPageLoading}
                manualSorting={false}
                manualPagination={false}
                pagination={false}
                customHeight="max-h-none"
              />
            </CollapsibleSection>
          ))}
        </div>
      </div>
    );
  }
);

TuitionStep.displayName = 'TuitionStep';

export default TuitionStep;
